import { Combobox } from "@/components/flexkit/combobox/Combobox";
import { useListProjectsPaginated, useProject, type PlainProject } from "@/queries/project-queries";
import { useCallback, useMemo, useState, type FC } from "react";
import { useDebounce } from "use-debounce";

const allProjectsItem = { id: "all", metadata: { name: "All Projects" } } as PlainProject;

type ProjectComboboxProps = {
    selectedProject: string | null;
    onProjectChange: (value: string) => void;
    className?: string;
    disabled?: boolean;
    excludeProjectIds?: string[];
    placeholder?: string;
};

/**
 * Self-contained project combobox with backend search and infinite scroll.
 * Includes "All Projects" as a special option.
 */
export const ProjectCombobox: FC<ProjectComboboxProps> = ({
    selectedProject,
    onProjectChange,
    className,
    disabled = false,
    excludeProjectIds,
    placeholder = "All Projects",
}) => {
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue] = useDebounce(searchValue, 250, {
        trailing: true,
    });

    const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isPending, isFetching } = useListProjectsPaginated({
        search: debouncedSearchValue,
    });
    const { data: selectedProjectData } = useProject(selectedProject ?? undefined);

    const projectsData = useMemo(() => {
        if (!data) return { projects: [] };
        return {
            projects: data.pages.flatMap((page) => page.projects),
        };
    }, [data]);

    // Filter out excluded projects
    const availableProjects = useMemo(() => {
        if (!excludeProjectIds || excludeProjectIds.length === 0) return projectsData.projects;
        return projectsData.projects.filter((project) => !excludeProjectIds.includes(project.id));
    }, [projectsData.projects, excludeProjectIds]);

    const selectedProjectName = useMemo(() => {
        if (!selectedProject) return undefined;

        const foundProject = projectsData.projects.find((p) => p.id === selectedProject);
        if (foundProject) return foundProject.metadata?.name;

        return selectedProjectData?.metadata?.name;
    }, [selectedProject, projectsData.projects, selectedProjectData?.metadata?.name]);

    const handleScrollEnd = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const projectsWithAll = useMemo(() => {
        // Don't show "All Projects" when filtering/excluding projects (i.e., in "add mode")
        if (searchValue || excludeProjectIds) {
            return availableProjects;
        }

        return [allProjectsItem, ...availableProjects];
    }, [searchValue, excludeProjectIds, availableProjects]);

    return (
        <Combobox
            value={selectedProject ?? "all"}
            onValueChange={onProjectChange}
            filterPlaceholder="Search projects..."
            disabled={disabled || (projectsData.projects.length === 0 && !isPending)}
            className={className}
            loading={isPending || isFetchingNextPage}
        >
            <Combobox.Value>
                <Combobox.ValueLabel>
                    {selectedProject && selectedProjectName ? selectedProjectName : placeholder}
                </Combobox.ValueLabel>
            </Combobox.Value>
            <Combobox.Popover className="z-[999] w-96 max-w-[calc(100vw-1rem)]" sameWidth={false}>
                <Combobox.SearchBox onValueChanged={setSearchValue} loading={isFetching || isFetchingNextPage} />
                <Combobox.List
                    onScrollEnd={handleScrollEnd}
                    scrollEndThreshold={1000}
                    items={projectsWithAll}
                    searchKeys={[]}
                    disableFiltering
                    noMatchesComponent={<Combobox.Empty>No projects found</Combobox.Empty>}
                >
                    {(project) => (
                        <Combobox.ListItem key={project.id} value={project.id}>
                            <Combobox.ListItemTitle>{project.metadata?.name || project.id}</Combobox.ListItemTitle>
                        </Combobox.ListItem>
                    )}
                </Combobox.List>
            </Combobox.Popover>
        </Combobox>
    );
};
